import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import ApplicationCard from "@/components/ApplicationCard";
import { useTrackView } from "@/lib/useTrackView";
import { breadcrumbSchema } from "@/lib/schema";
import FinalCta from "@/sections/FinalCta";

// Turkish locative forms ("…'de Yaptığımız Uygulamalar") for the 10 Kocaeli districts.
const LOCATIVE = {
  izmit: "İzmit'te",
  basiskele: "Başiskele'de",
  kartepe: "Kartepe'de",
  golcuk: "Gölcük'te",
  derince: "Derince'de",
  korfez: "Körfez'de",
  gebze: "Gebze'de",
  darica: "Darıca'da",
  cayirova: "Çayırova'da",
  dilovasi: "Dilovası'nda",
};

export default function ApplicationsDistrict({ data }) {
  useTrackView("application", data.district_slug);
  const loc = LOCATIVE[data.district_slug] || `${data.district} bölgesinde`;
  const h1 = `${loc} Yaptığımız Uygulamalar`;
  const apps = data.applications || [];

  const crumbs = [
    { label: "Ana Sayfa", to: "/" },
    { label: "Uygulamalar", to: "/uygulamalar" },
    { label: data.district },
  ];

  return (
    <>
      <Seo
        title={`${data.district} Araç Uygulamaları | Gerçek Sonuçlar - Meguiar's İzmit`}
        description={`${loc} gerçekleştirdiğimiz araç koruma ve bakım uygulamaları. Seramik kaplama, PPF, detaylı iç temizlik ve daha fazlasının öncesi/sonrası sonuçları.`}
        canonicalPath={`/uygulamalar/${data.district_slug}`}
        image={data.hub?.hero_image}
        jsonLd={breadcrumbSchema(crumbs)}
      />
      <PageHero
        overline="Uygulamalar"
        title={h1}
        subtitle={`${data.district} ve çevresinde aracını bize getiren sürücüler için gerçekleştirdiğimiz uygulamalar. İşte bazı dönüşüm hikâyeleri.`}
        image={data.hub?.hero_image}
        breadcrumbs={crumbs}
      />

      <section className="obsidian py-14 sm:py-20">
        <div className="container-x">
          <div className="flex flex-wrap items-center gap-3">
            {data.hub && (
              <Link to={`/${data.hub.slug}`} data-testid="applications-district-hub-link" className="inline-flex items-center gap-2 border border-white/10 bg-white/[0.02] px-4 py-2 text-sm text-neutral-300 transition-colors hover:border-gold/40 hover:text-gold">
                <MapPin className="h-4 w-4 text-gold/70" /> {data.district} Hizmet Merkezi
              </Link>
            )}
            <Link to="/uygulamalar" data-testid="applications-all-link" className="inline-flex items-center gap-2 border border-white/10 bg-white/[0.02] px-4 py-2 text-sm text-neutral-300 transition-colors hover:border-gold/40 hover:text-gold">
              Tüm Uygulamalar <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          {apps.length === 0 ? (
            <p className="mt-10 text-neutral-500" data-testid="applications-district-empty">
              {data.district} bölgesinde henüz yayınlanmış uygulama bulunmuyor. Aracınız ilk olabilir — bizimle iletişime geçin.
            </p>
          ) : (
            <div className="mt-8 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {apps.map((a, i) => <ApplicationCard key={a.slug} app={a} index={i} />)}
            </div>
          )}
        </div>
      </section>
      <FinalCta />
    </>
  );
}
